import * as Sentry from '@sentry/nextjs';
import { serializeError } from 'serialize-error';
import {
  getVendorErrorContext,
  vendorErrorVarianceKey,
} from '@util/logVendorError';
import { FLOOD_VARIANCE_TAG } from '@util/sentryFloodGuard';

export type ErrorReportContext = {
  // Route the crash happened on. Passed explicitly (rather than relying only
  // on Sentry's HttpContext integration reading window.location.href) so it
  // survives even if the integration's automatic capture is ever reconfigured.
  route?: string;
  // 'guest' | 'authenticated' — read from the same 'auth' cookie the server
  // already checks in src/pages/api/error.ts, independent of AuthContext, so
  // it's available even when the crash is inside AuthProvider itself.
  authState?: string;
};

export const logError = async (
  err: Error | string | unknown,
  id = generateRandomId(),
  componentStack?: string,
  context?: ErrorReportContext
) => {
  try {
    // Always log to console for local debugging
    console.error(err);

    // Send to Sentry (handles its own environment checks in sentry.client.config.js)
    if (err instanceof Error) {
      // A failed vendor call attaches its redacted response body to the Error
      // (see logVendorError). Passing it here, rather than writing it to the
      // Sentry scope at throw time, keeps each body with its own event —
      // checkout fetches rates for cart items concurrently, so a shared scope
      // slot would let one product's failure overwrite another's. GB #1527.
      const vendorError = getVendorErrorContext(err);
      Sentry.captureException(err, {
        tags: {
          errorId: id,
          ...(context?.route ? { route: context.route } : {}),
          ...(context?.authState ? { authState: context.authState } : {}),
          // Every rate failure in a cart shares one flood-guard fingerprint, so
          // without variance a large cart's distinct bodies collapse into the
          // window's cap and most of them never arrive. GB #1527.
          ...(vendorError
            ? { [FLOOD_VARIANCE_TAG]: vendorErrorVarianceKey(vendorError) }
            : {}),
        },
        extra: {
          componentStack,
        },
        ...(vendorError ? { contexts: { vendor_error: vendorError } } : {}),
      });
    } else {
      Sentry.captureMessage(String(err), {
        level: 'error',
        tags: {
          errorId: id,
          ...(context?.route ? { route: context.route } : {}),
          ...(context?.authState ? { authState: context.authState } : {}),
        },
        extra: {
          originalError: err,
          componentStack,
        },
      });
    }

    // Also send to Google Cloud Error Reporting (existing behavior)
    // This provides redundancy and preserves existing monitoring.
    // Browser-only: Node fetch can't resolve relative URLs, so calling this
    // from `_error.tsx`'s server-side getInitialProps used to throw
    // ERR_INVALID_URL. Server code paths use logApiError.ts (absolute URL).
    if (
      typeof window !== 'undefined' &&
      process.env.NEXT_PUBLIC_VERCEL_ENV === 'production'
    ) {
      const ctx = serializeError(err);
      if (componentStack) ctx.stack = componentStack;
      await fetch('/api/error', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...ctx, id }),
      });
    }
  } catch (e) {
    console.error('Error reporting error', e);
  }
};

export const generateRandomId = () => {
  const chars =
    'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
  let result = '';
  for (let i = 0; i < 24; i++) {
    result += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return result;
};

// The 'auth' cookie's PRESENCE does not mean the visitor is authenticated:
// AuthContext.tsx only removes it on an EXPLICIT logout (GB #1435) — a
// Firebase session that goes null without one (revoked token, tab resume)
// leaves the cookie in place for up to its full 24h lifetime, while the ID
// token it carries is only valid for 1h (GB #677, also deliberate — see
// authCookieOptions.ts). So a stale cookie is common, not an edge case, and
// counting it as "authenticated" would bias guest-vs-authenticated crash
// telemetry toward under-counting guests. This reads the token's own `exp`
// claim to tell a live session from a stale cookie. No signature check: this
// is a telemetry classification, not an auth decision, and never throws —
// any parse failure is treated as "not a live session".
//
// KNOWN LIMITATION (flagged in PR #1737 review, left as-is — see PR for
// Bill's call): a cookie can be unexpired yet AuthContext already be
// rendering `user: null` — e.g. mid-flight during the documented iOS Safari
// tab-resume transient-null race (AuthContext.tsx ~line 526). This function
// has no way to see that: it only reads the cookie's own claims, by design,
// so it still works when the crash is INSIDE AuthProvider itself (its React
// context is unreachable from the root ErrorBoundary in that case). Closing
// this gap would mean reading AuthContext's live state instead, which
// reintroduces exactly that fragility for a narrow, self-correcting window —
// not a strict improvement, and touches GB #1435/#677-hardened code for a
// telemetry nuance rather than a functional bug.
export const isAuthTokenLive = (token: string): boolean => {
  try {
    const payloadSegment = token.split('.')[1];
    if (!payloadSegment) return false;
    const base64 = payloadSegment.replace(/-/g, '+').replace(/_/g, '/');
    const padded = base64 + '='.repeat((4 - (base64.length % 4)) % 4);
    const payload = JSON.parse(atob(padded));
    return typeof payload.exp === 'number' && payload.exp * 1000 > Date.now();
  } catch {
    return false;
  }
};
